import { User } from "oidc-client-ts";
import { useAuth } from "react-oidc-context";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

import {
  allCarrierListColumnKeys,
  carrierListColumns,
  CarrierListColumnKey,
} from "components/CarrierList/List/carrierListColumns";

const getLocalStorageKey = (user?: User | null) => {
  const firstName = user?.profile?.firstName || "";
  const lastName = user?.profile?.lastName || "";

  return `${firstName}_${lastName}_profiles-carrier-columns`;
};

const isKnownColumnKey = (key: string): key is CarrierListColumnKey =>
  allCarrierListColumnKeys.includes(key as CarrierListColumnKey);

const useCarrierListColumns = (): [
  CarrierListColumnKey[],
  Dispatch<SetStateAction<CarrierListColumnKey[]>>,
] => {
  const { user } = useAuth();

  const [visibleColumns, setVisibleColumns] = useState<CarrierListColumnKey[]>(
    () => {
      const storedData = localStorage.getItem(getLocalStorageKey(user));

      if (!storedData) {
        return allCarrierListColumnKeys;
      }

      try {
        const storedColumns: unknown = JSON.parse(storedData);

        if (!Array.isArray(storedColumns)) {
          return allCarrierListColumnKeys;
        }

        const storedKeys = storedColumns.filter(
          (key): key is CarrierListColumnKey =>
            typeof key === "string" && isKnownColumnKey(key)
        );

        return carrierListColumns
          .filter(
            (column) => column.isAlwaysVisible || storedKeys.includes(column.key)
          )
          .map((column) => column.key);
      } catch {
        return allCarrierListColumnKeys;
      }
    }
  );

  useEffect(() => {
    if (user) {
      localStorage.setItem(
        getLocalStorageKey(user),
        JSON.stringify(visibleColumns)
      );
    }
  }, [visibleColumns, user]);

  return [visibleColumns, setVisibleColumns];
};

export default useCarrierListColumns;
